import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { Typography } from "@platform/design-system";

interface TriangleIconProps {
  variant: "top-left" | "top-right";
  color: string;
  size?: number;
}

const TriangleIcon: React.FC<TriangleIconProps> = ({
  variant,
  color,
  size = 8,
}) => (
  <Box
    sx={{
      width: 0,
      height: 0,
      borderStyle: "solid",
      borderWidth:
        variant === "top-left"
          ? `${size}px ${size}px 0 0`
          : `${size}px 0 0 ${size}px`,
      borderColor: `${color} transparent transparent transparent`,
    }}
  />
);

interface CircleBackgroundProps {
  bgColor?: string;
  children?: React.ReactNode;
}

const CircleBackground: React.FC<CircleBackgroundProps> = ({
  bgColor,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      borderRadius: "50%",
      backgroundColor: bgColor ?? "grey.200",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

interface LegendItem {
  icon: React.ReactNode;
  label: string;
  bgColor?: string;
}

interface AiPaletteColors {
  extracted?: string;
  comment?: string;
  reviewBackground?: string;
  validatedBackground?: string;
  extractionError?: string;
  edited?: string;
}

const defaultAiPalette: AiPaletteColors = {
  extracted: "success.main",
  comment: "info.main",
  reviewBackground: "warning.light",
  validatedBackground: "success.light",
  extractionError: "error.main",
  edited: "brandPrimary.main",
};

interface TableLegendProps {
  mode?: "ai" | "benchmark";
}

export const TableLegend: React.FC<TableLegendProps> = ({ mode = "ai" }) => {
  const theme = useTheme();
  const aiPalette =
    (theme.palette.ai as AiPaletteColors | undefined) ?? defaultAiPalette;

  const aiLegendItems: LegendItem[] = [
    {
      icon: <TriangleIcon variant="top-left" color={aiPalette.extracted} />,
      label: "AI Extracted",
    },
    {
      icon: (
        <WorkspacePremiumOutlinedIcon
          sx={{ color: aiPalette.extracted, fontSize: 14 }}
        />
      ),
      label: "High Confidence",
    },
    {
      icon: <TriangleIcon variant="top-right" color={aiPalette.comment} />,
      label: "Comment",
    },
    {
      icon: null,
      label: "Needs Review",
      bgColor: aiPalette.reviewBackground,
    },
    {
      icon: null,
      label: "Validated",
      bgColor: aiPalette.validatedBackground,
    },
    {
      icon: (
        <WarningAmberIcon
          sx={{ color: aiPalette.extractionError, fontSize: 14 }}
        />
      ),
      label: "Mapping Error",
    },
    {
      icon: <EditOutlinedIcon sx={{ color: aiPalette.edited, fontSize: 14 }} />,
      label: "Edited",
    },
    {
      icon: (
        <Typography variant="caption" color="textSecondary">
          <strong>{`[ ]`}</strong>
        </Typography>
      ),
      label: "No Data Found",
    },
  ];

  const benchmarkLegendItems: LegendItem[] = [
    {
      icon: (
        <TriangleIcon
          variant="top-left"
          color={theme.palette.brandPrimary.main}
        />
      ),
      label: "Benchmark",
    },
    {
      icon: <TriangleIcon variant="top-right" color={aiPalette.comment} />,
      label: "Comment",
    },
    {
      icon: null,
      label: "Needs Review",
      bgColor: aiPalette.reviewBackground,
    },
    {
      icon: null,
      label: "Validated",
      bgColor: aiPalette.validatedBackground,
    },
    {
      icon: <EditOutlinedIcon sx={{ color: aiPalette.edited, fontSize: 14 }} />,
      label: "Edited",
    },
  ];

  const legendItems =
    mode === "benchmark" ? benchmarkLegendItems : aiLegendItems;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1,
        border: "1px solid",
        borderColor: "divider",
        flexWrap: "wrap",
      }}
    >
      <Typography
        variant="body-medium"
        sx={{ flexShrink: 0 }}
        color="textSecondary"
      >
        <strong>Legend:</strong>
      </Typography>

      {legendItems.map(({ icon, label, bgColor }) => (
        <Box
          key={label}
          sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
        >
          <CircleBackground bgColor={bgColor}>{icon}</CircleBackground>
          <Typography variant="body-medium" color="textSecondary">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default TableLegend;
